import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaEnvelope, FaFacebookF, FaTwitter, FaLinkedinIn,
  FaMapMarkerAlt, FaPhoneAlt, FaClock, FaDirections
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import SectionTitle from "../components/layout/SectionTitle";
import PageHero from "../components/layout/PageHero";

export default function Contact() {
  const { t } = useTranslation();

  // Mise à jour du titre de la page
  React.useEffect(() => {
    document.title = `${t('header.contact')} | SOCABEG`;
  }, [t]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [isSending, setIsSending] = useState(false);
  const [feedback, setFeedback] = useState("");

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSending(true);
    setFeedback("");

    try {
      // Utiliser l'URL du serveur depuis la configuration.
      // En production, éviter tout fallback sur localhost (qui pointerait vers la machine du visiteur).
      const configuredApiUrl = process.env.REACT_APP_API_URL;
      const apiBaseUrl = configuredApiUrl
        ? configuredApiUrl.replace(/\/$/, "")
        : (process.env.NODE_ENV === "development" ? "http://localhost:5001" : "");

      const res = await fetch(`${apiBaseUrl}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData
        })
      });

      const data = await res.json();

      if (res.ok) {
        setFeedback("✅ Votre message a été envoyé avec succès. Vous recevrez bientôt une confirmation par email.");
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        if (data.message) {
          setFeedback(`❌ ${data.message}`);
        } else {
          setFeedback("❌ Une erreur est survenue. Veuillez réessayer.");
        }
      }
    } catch (error) {
      setFeedback("❌ Erreur réseau. Serveur injoignable.");
      console.error("Erreur lors de l'envoi du formulaire:", error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <PageHero 
        backgroundImage="/images/hero_contact.png"
        title={t('contact.hero.title')}
        subtitle={t('contact.hero.subtitle')}
        overlayColor="socabeg-blue"
        textPosition="left"
      />
      <section className="bg-white pt-8">
        <div className="container mx-auto px-4">
          <SectionTitle 
            overline={t('contact.title')} 
            highlight={t('contact.highlight')}
            showLogo={true}
            align="left"
          />
          <p className="text-xl text-socabeg-dark-gray/80 max-w-3xl mb-0">
            {t('contact.description')}
          </p>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Infos de contact */}
          <motion.div
            className="bg-white rounded-lg shadow-sm p-8 flex flex-col justify-between border-2 border-transparent transition-all duration-300"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div>
              <h2 className="text-2xl font-bold text-socabeg-blue mb-6">{t('contact.coordinates')}</h2>
              <div className="space-y-6">
                {/* Adresse */}
                <div className="flex items-start">
                  <div className="w-12 h-12 min-w-[3rem] rounded-full bg-socabeg-blue flex items-center justify-center text-white mr-4 aspect-square">
                    <FaMapMarkerAlt size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{t('contact.address.title')}</h3>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=14.6778611,-17.4637778"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-socabeg-dark-gray/80 hover:text-socabeg-blue block"
                    >
                      Fann-Hock – Corniche face Cour Suprême, BP 1270, Dakar, Sénégal
                    </a>
                  </div>
                </div>

                {/* Téléphone */}
                <div className="flex items-start">
                  <div className="w-12 h-12 min-w-[3rem] rounded-full bg-socabeg-blue flex items-center justify-center text-white mr-4 aspect-square">
                    <FaPhoneAlt size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{t('contact.phone.title')}</h3>
                    <a href="tel:+221338893535" className="block hover:text-socabeg-blue">+221 33 889 35 35</a>
                    <a href="tel:+221338893099" className="block hover:text-socabeg-blue">+221 33 889 30 99</a>
                    <a href="tel:+221708255522" className="block hover:text-socabeg-blue">+221 70 825 55 22</a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start">
                  <div className="w-12 h-12 min-w-[3rem] rounded-full bg-socabeg-blue flex items-center justify-center text-white mr-4 aspect-square">
                    <FaEnvelope size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{t('contact.email.title')}</h3>
                    <a href="mailto:infos@socabeg.sn" className="block hover:text-socabeg-blue">infos@socabeg.sn</a>
                  </div>
                </div>

                {/* Horaires */}
                <div className="flex items-start">
                  <div className="w-12 h-12 min-w-[3rem] rounded-full bg-socabeg-blue flex items-center justify-center text-white mr-4 aspect-square">
                    <FaClock size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{t('contact.hours.title')}</h3>
                    <p className="text-socabeg-dark-gray/80">
                      {t('contact.hours.value').split('\n').map((line, i) => (
                        <span key={i}>
                          {line}
                          {i < t('contact.hours.value').split('\n').length - 1 && <br />}
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
              </div>

              {/* Réseaux sociaux */}
              <div className="mt-8 pt-8 border-t border-socabeg-gray-light">
                <h3 className="text-lg font-semibold mb-4">{t('contact.social')}</h3>
                <div className="flex space-x-4">
                  <a href="https://www.facebook.com/socabeg" className="h-10 w-10 rounded-full bg-socabeg-blue text-white flex items-center justify-center hover:bg-socabeg-red">
                    <FaFacebookF />
                  </a>
                  <a href="https://www.twitter.com/socabeg" className="h-10 w-10 rounded-full bg-socabeg-blue text-white flex items-center justify-center hover:bg-socabeg-red">
                    <FaTwitter />
                  </a>
                  <a href="https://www.linkedin.com/company/socabeg" className="h-10 w-10 rounded-full bg-socabeg-blue text-white flex items-center justify-center hover:bg-socabeg-red">
                    <FaLinkedinIn />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Formulaire de contact */}
          <motion.div
            className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col border-2 border-transparent transition-all duration-300"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="p-6">
              <h2 className="text-2xl font-bold text-socabeg-blue mb-6">{t('contact.form.title')}</h2>
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-socabeg-dark-gray mb-1">{t('contact.form.name')}</label>
                  <input
                    type="text"
                    name="name"
                    placeholder={t('contact.form.name.placeholder')}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-socabeg-dark-gray mb-1">{t('contact.form.email')}</label>
                  <input
                    type="email"
                    name="email"
                    placeholder={t('contact.form.email.placeholder')}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border rounded-md"
                />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-socabeg-dark-gray mb-1">{t('contact.form.phone')}</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder={t('contact.form.phone.placeholder')}
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-socabeg-dark-gray mb-1">{t('contact.form.subject')}</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border rounded-md text-gray-500"
                  >
                    <option value="">{t('contact.form.subject.placeholder')}</option>
                    <option value="quote">{t('contact.form.subject.quote')}</option>
                    <option value="information">{t('contact.form.subject.information')}</option>
                    <option value="partnership">{t('contact.form.subject.partnership')}</option>
                    <option value="other">{t('contact.form.subject.other')}</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-socabeg-dark-gray mb-1">{t('contact.form.message')}</label>
                  <textarea
                    name="message"
                    rows="4"
                    placeholder={t('contact.form.message.placeholder')}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border rounded-md"
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  disabled={isSending}
                  className="w-full md:w-auto px-6 py-3 bg-socabeg-blue text-white font-medium rounded-md hover:bg-socabeg-blue/90 transition-colors duration-300 flex items-center justify-center"
                >
                  <FaEnvelope className="mr-2" />
                  {isSending ? t('contact.form.sending') : t('contact.form.send')}
                </button>

                {feedback && (
                  <div className="mt-4 text-sm text-center text-socabeg-dark-gray">
                    {feedback}
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>

        {/* Carte Google Maps */}
        <div className="mt-12">
          <motion.div
            className="bg-white rounded-lg shadow-sm overflow-hidden border-2 border-transparent transition-all duration-300"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="p-6 pb-0">
              <h2 className="text-2xl font-bold text-socabeg-blue mb-6">{t('contact.location.title')}</h2>
            </div>
            <div className="w-full h-96 overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3859.0965837902984!2d-17.46646492414567!3d14.677861077478172!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTTCsDQwJzQwLjMiTiAxN8KwMjcnNDkuNiJX!5e0!3m2!1sfr!2sfr!4v1622731282693!5m2!1sfr!2sfr"
                width="100%" height="100%" style={{ border: 0 }}
                allowFullScreen loading="lazy" title="Localisation SOCABEG"
              ></iframe>
            </div>
            <div className="p-6 bg-white">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold mb-2">{t('contact.headquarters')}</h3>
                </div>
                <div className="flex justify-start lg:justify-end items-center">
                  <a
                    href="https://www.google.com/maps/dir//14.6778611,-17.4637778/"
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 py-3 bg-socabeg-blue text-white rounded-md hover:bg-socabeg-blue/80 transition-colors duration-200"
                  >
                    <FaDirections />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      </section>
    </div>
  );
}
